import React, { useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ShieldCheck } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import { Button } from "@/components/ui/button";

/**
 * Solid-background responsive header with language switcher & mobile menu.
 * Language switch uses navigate(..., { state: { langSwitch: true } }) so
 * ScrollToTop can skip scrolling on that transition.
 */
export default function Header() {
  const { t, lang } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const langPrefix = `/${lang}`;
  const otherLang = lang === "mx" ? "en" : "mx";

  const switchLanguage = () => {
    // Rebuild the equivalent path in the other language.
    const map = {
      "/mx": "/en",
      "/en": "/mx",
      "/mx/privacidad": "/en/privacy",
      "/en/privacy": "/mx/privacidad",
      "/mx/cookies": "/en/cookies",
      "/en/cookies": "/mx/cookies",
      "/mx/terminos": "/en/terms",
      "/en/terms": "/mx/terminos",
    };
    const target = map[location.pathname] || `/${otherLang}`;
    navigate(target, { state: { langSwitch: true } });
    setOpen(false);
  };

  const navItems = [
    { to: langPrefix, key: "header.nav_home", anchor: null },
    { to: `${langPrefix}#apoyo`, key: "header.nav_apoyo", anchor: "apoyo" },
    { to: `${langPrefix}#educacion`, key: "header.nav_educacion", anchor: "educacion" },
  ];

  const goHome = (e) => {
    e.preventDefault();
    if (location.pathname !== langPrefix) {
      navigate(langPrefix);
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    }
    setOpen(false);
  };

  const goAnchor = (hash) => (e) => {
    e.preventDefault();
    const id = hash.replace("#", "");
    const scrollToId = () => {
      // Compute the target position after any layout shift (e.g. the mobile
      // menu closing) so the final scrollY lands on the section top.
      const el = document.getElementById(id);
      if (!el) return;
      const top =
        el.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top, behavior: "smooth" });
    };
    if (location.pathname !== langPrefix) {
      navigate(langPrefix);
      // Wait for the page to mount before scrolling to the anchor.
      setTimeout(scrollToId, 120);
    } else {
      // Close the mobile menu first, then scroll on the next frame so the
      // layout has settled and the computed offset is accurate.
      setOpen(false);
      requestAnimationFrame(() => requestAnimationFrame(scrollToId));
      return;
    }
    setOpen(false);
  };

  const handleNavClick = (item) => (e) => {
    if (item.anchor) return goAnchor(item.anchor)(e);
    return goHome(e);
  };

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 w-full bg-[#0F2942] text-white border-b border-white/10 shadow-sm"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 lg:h-20 items-center justify-between gap-4">
          <Link
            to={langPrefix}
            data-testid="header-logo-link"
            className="flex items-center gap-3 shrink-0"
          >
            <span
              aria-hidden
              className="flex h-10 w-10 items-center justify-center rounded-md bg-[#059669] text-white"
            >
              <ShieldCheck size={22} strokeWidth={2.4} />
            </span>
            <span className="flex flex-col leading-none">
              <span className="text-lg font-bold tracking-tight">
                {t("header.brand")}
              </span>
              <span className="text-[11px] uppercase tracking-widest text-white/70">
                {t("header.brand_tag")}
              </span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav
            aria-label="Primary"
            className="hidden lg:flex items-center gap-8"
          >
            {navItems.map((item) => (
              <a
                key={item.key}
                href={item.to}
                onClick={handleNavClick(item)}
                data-testid={`nav-${item.key.replace(/\./g, "-")}`}
                className="text-sm font-medium text-white/80 hover:text-white transition-colors"
              >
                {t(item.key)}
              </a>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <button
              type="button"
              data-testid="lang-switch"
              onClick={switchLanguage}
              aria-label={t("header.lang_switch_aria")}
              className="rounded-full border border-white/20 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/10 transition-colors"
            >
              {lang === "mx" ? t("header.lang_en") : t("header.lang_es")}
            </button>
            <Button
              data-testid="header-cta"
              onClick={goAnchor("newsletter")}
              className="bg-[#059669] hover:bg-[#047857] text-white rounded-full px-5"
            >
              {t("header.cta_consulta")}
            </Button>
          </div>

          {/* Mobile burger */}
          <div className="lg:hidden flex items-center gap-2">
            <button
              type="button"
              data-testid="lang-switch-mobile"
              onClick={switchLanguage}
              aria-label={t("header.lang_switch_aria")}
              className="rounded-full border border-white/20 px-3 py-1.5 text-xs font-semibold text-white"
            >
              {lang === "mx" ? "EN" : "ES"}
            </button>
            <button
              type="button"
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? t("header.menu_close") : t("header.menu_open")}
              className="flex h-10 w-10 items-center justify-center rounded-md border border-white/20 text-white"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {open && (
          <div
            data-testid="mobile-menu"
            className="lg:hidden pb-4 pt-2 border-t border-white/10"
          >
            <nav aria-label="Mobile" className="flex flex-col gap-1">
              {navItems.map((item) => (
                <a
                  key={item.key}
                  href={item.to}
                  onClick={handleNavClick(item)}
                  data-testid={`mobile-nav-${item.key.replace(/\./g, "-")}`}
                  className="px-2 py-3 rounded-md text-base font-medium text-white/90 hover:bg-white/5"
                >
                  {t(item.key)}
                </a>
              ))}
              <Button
                data-testid="mobile-header-cta"
                onClick={goAnchor("newsletter")}
                className="mt-3 bg-[#059669] hover:bg-[#047857] text-white rounded-full"
              >
                {t("header.cta_consulta")}
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
